import React, { useState } from 'react'
import {
  Step,
  Stepper,
  Button,
  Typography,
  Box,
  Grid,
  Stack,
  ButtonProps,
  CircularProgress,
  Collapse,
  Alert,
  StepLabel,
  Grow,
  Link,
} from '@mui/material'
import {
  Link as RouterLink,
  LinkProps,
  useLocation,
  useSearchParams,
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { styled } from '@mui/material/styles'
import { useForm } from 'react-hook-form'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { set } from 'lodash'

import { SOURCES } from 'helpers/constants'
import { EstateAgentUserDTO } from 'api/services/Agent/types'
import Step1 from './Step1'
import Step2 from './Step2'
import { agentTemplate } from './constants'
import AgentService, { useCreateAgent } from 'api/services/Agent'

import {
  Attachment,
  ContactInfo,
  I18nAddress,
  I18nText,
  SocialMediaInfo,
  User,
  FilePathWithPreview,
  AttachmentType,
  Source,
} from 'types'
import { grey } from '@mui/material/colors'
import usePageView from 'hooks/usePageView'

const LinkButton = styled(Button)<LinkProps>(({ theme }) => ({
  padding: '10px 12px',
  minWidth: 150,
  fontSize: 16,
  textTransform: 'none',
  [theme.breakpoints.up('md')]: {
    fontSize: 20,
  },
}))
export const StyledButton = styled(Button)<ButtonProps>(({ theme }) => ({
  padding: '10px 12px',
  minWidth: 150,
  fontSize: 16,
  textTransform: 'none',
  [theme.breakpoints.up('md')]: {
    fontSize: 20,
  },
}))

export interface FormValues {
  termsAgreement: boolean
  validationError: string
  user: User
  address: I18nAddress
  partners: ContactInfo[]
  companyName: I18nText
  companyTitle: I18nText
  description: string
  comments: string
  externalId: string
  estateAgentSystem: Source | ''
  vatNumber: string
  companyCommercialId: string
  taxOffice: string
  contactInfo: ContactInfo
  socialMedia: {
    Website: SocialMediaInfo
    Facebook: SocialMediaInfo
    Instagram: SocialMediaInfo
    Video: SocialMediaInfo
  }
  attachments: {
    logo: FilePathWithPreview | null
    banner: FilePathWithPreview | null
  }
}
interface AgentInfo {
  id: number | string
  alternativeId: number | string
  externalId: number | string
  dateTimeCreated: any
  dateTimeUpdated: any
  entityStatus: any
  entityOwner: any
  systemName: string
  systemSourceCode: string
  agentExternalIdType: string
  active: boolean
  useGenericApi: boolean
}
const DEFAULT_VALUES = {
  termsAgreement: false,
  validationError: '',
  user: {
    username: '',
    password: '',
    person: {
      firstName: '',
      lastName: '',
    },
  },
  companyName: {
    defaultValue: false,
    id: null,
    locale: 'el',
    text: '',
  },
  companyTitle: {
    defaultValue: false,
    id: null,
    locale: 'el',
    text: '',
  },
  companyCommercialId: '',
  vatNumber: '',
  taxOffice: '',
  source: '' as Source,
  // source: '' as SourceType,
  externalId: '',
  comments: '',
  description: '',
  contactInfo: {
    contactName: '',
    email: '',
    landLineNumber: '',
    landLineNumber2: '',
    mobileNumber: '',
    mobileNumber2: '',
    id: null,
    locale: 'el',
    attachments: [],
    location: {
      addresses: [],
      id: null,
    },
  },
  address: {
    addressDescription: '',
    area: '',
    city: '',
    country: 'Ελλάδα',
    id: null,
    postalCode: '',
    street: '',
    streetNumber: '',
    locale: 'el',
  },
  partners: [],
  socialMedia: {
    Website: {
      id: null,
      mediaTitle: 'Website',
      url: '',
    },
    Facebook: {
      id: null,
      mediaTitle: 'Facebook',
      url: '',
    },
    Instagram: {
      id: null,
      mediaTitle: 'Instagram',
      url: '',
    },
    Video: {
      id: null,
      mediaTitle: 'Video',
      url: '',
    },
  },
  attachments: {
    logo: null,
    banner: null,
  },
}

function SignUpPage() {
  usePageView()
  const { enqueueSnackbar } = useSnackbar()

  const { executeRecaptcha } = useGoogleReCaptcha()

  const createGoogleToken = React.useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return
    }
    return await executeRecaptcha()
  }, [executeRecaptcha])

  const [searchParams] = useSearchParams()
  const location = useLocation()

  const [activeStep, setActiveStep] = React.useState(0)

  const [localIsError, setLocalIsError] = React.useState(false)

  const handleCloseError = () => {
    setLocalIsError(false)
  }

  const [completed] = React.useState<{
    [k: number]: boolean
  }>({})
  const { t } = useTranslation()

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    resetField,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
  })
  const steps = [
    {
      label: 'signUpPage.steps.step1.label',
      component: (
        <Step1
          control={control}
          getValues={getValues}
          resetField={resetField}
        />
      ),
      control: control,
    },
    {
      label: 'signUpPage.steps.step2.label',
      component: (
        <Step2
          control={control}
          getValues={getValues}
          setValue={setValue}
          resetField={resetField}
        />
      ),
      control: control,
    },
  ]

  const FROM = searchParams.get(`from`)

  const createAgentMutation = useCreateAgent({
    onMutate: () => {
      handleCloseError()
    },
    onSuccess: () => {
      enqueueSnackbar(t('signUpPage.successMessage'), {
        variant: 'success',
      })
    },
    onError: (error) => {
      enqueueSnackbar(t('signUpPage.failedMessage'), {
        variant: 'error',
      })
    },
  })

  const isLastStep = () => {
    return activeStep === totalSteps() - 1
  }
  const handleNext = async () => {
    if (activeStep === 0) {
      const isValid = await trigger(['user'])
      if (isValid) {
        setActiveStep(activeStep + 1)
      }
    }
    if (activeStep === 1) {
      handleSubmit(onSubmit)()
    }
  }

  const [agentInfo, setAgentInfo] = useState<AgentInfo[]>([])

  const estateAgentService = AgentService.hooks.useEstateAgents({
    onSuccess: (data) => {
      setAgentInfo(data)
    },
  })

  const onSubmit = async (data) => {
    const estateAgentPayload = () => {
      const matchedAgent = agentInfo?.find(
        (item) => item.systemSourceCode === data.estateAgentSystem
      )

      if (matchedAgent) {
        return {
          id: matchedAgent.id,
          systemName: matchedAgent.systemName,
          systemSourceCode: matchedAgent.systemSourceCode,
          agentExternalIdType: matchedAgent.agentExternalIdType,
          active: matchedAgent.active,
          useGenericApi: matchedAgent.useGenericApi,
        }
      }

      return null
    }
    const token = await createGoogleToken()

    const payload: EstateAgentUserDTO = Object.assign({}, agentTemplate)

    const generateByteArrayImages = async (): Promise<Attachment[]> => {
      const parsedPhotos: Attachment[] = []

      const imageFields = Object.entries(data.attachments)
      for (let i = 0; i < imageFields?.length; i++) {
        const file = imageFields[i][1] as File
        if (file) {
          const ab = await file.arrayBuffer()
          const ui8a = new Uint8Array(ab)
          const arrayData = Array.from(ui8a)

          const temp: Attachment = {
            id: null,
            alternativeId: null,
            externalId: null,
            dateTimeCreated: null,
            dateTimeUpdated: null,
            entityStatus: null,
            entityOwner: null,
            attachmentType: imageFields[i][0]?.toUpperCase() as AttachmentType,
            filePath: null,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
            fileData: arrayData,
          }
          parsedPhotos.push(temp)
        }
      }
      return parsedPhotos
    }

    const attachments = await generateByteArrayImages()

    set(payload, 'estateAgent.attachments', attachments)

    const generateSocialMedia = (data) => {
      const socialArr: SocialMediaInfo[] = []
      Object.entries(data).forEach((entry) => {
        const [key, value] = entry
        if (data[`${key}`]?.url?.trim()) {
          socialArr.push(value as SocialMediaInfo)
        }
      })
      return socialArr
    }

    const generateContactInfos = (): ContactInfo[] => {
      const cInfos: ContactInfo[] = []
      if (
        payload.estateAgent?.cInfos?.length &&
        payload.estateAgent?.cInfos[0]
      ) {
        cInfos.push({ ...payload.estateAgent?.cInfos[0], ...data.contactInfo })
        set(
          payload,
          'estateAgent.cInfos[0].socialMediaInfo',
          generateSocialMedia(data.socialMedia)
        )
        return [...cInfos, ...data.partners]
      }
      return cInfos
    }
    set(payload, 'estateAgent.cInfos', generateContactInfos())

    set(payload, 'estateAgent.cInfos[0].contactName', data.companyTitle?.text)
    set(payload, 'estateAgent.cInfos[0].description', data.comments)
    set(payload, 'estateAgent.cInfos[0].location.addresses[0]', data.address)
    set(payload, 'estateAgent.comments', data.comments)
    set(payload, 'estateAgent.description', data.description)
    set(payload, 'estateAgent.companyName[0]', data.companyName)
    set(payload, 'estateAgent.companyTitle[0]', data.companyTitle)
    set(payload, 'estateAgent.companyName[0]', data.companyName)
    set(payload, 'estateAgent.cInfos[0].email', data.user.username)
    set(
      payload,
      'estateAgent.cInfos[0].landLineNumber',
      data.contactInfo.landLineNumber
    )
    set(
      payload,
      'estateAgent.cInfos[0].landLineNumber2',
      data.contactInfo.landLineNumber2
    )
    set(
      payload,
      'estateAgent.cInfos[0].mobileNumber',
      data.contactInfo.mobileNumber
    )
    set(
      payload,
      'estateAgent.cInfos[0].mobileNumber2',
      data.contactInfo.mobileNumber2
    )
    set(
      payload,
      'estateAgent.cInfos[0].contactName',
      data.contactInfo.contactName
    )
    set(payload, 'estateAgent.cInfos[0].description', data.description)
    set(payload, 'estateAgent.estateAgentSystem', await estateAgentPayload())
    if ([SOURCES.ORBIT, SOURCES.FORTUNET].includes(data.source)) {
      set(
        payload,
        'estateAgent.externalId',
        data.externalId ? data.externalId : null
      )
    }
    set(payload, 'estateAgent.companyCommercialId', data.companyCommercialId)
    set(payload, 'estateAgent.vatNumber', data.vatNumber)
    set(payload, 'user', data.user)

    set(payload, 'token', token)

    createAgentMutation.mutate(payload)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const completedSteps = () => {
    return Object.keys(completed).length
  }
  const totalSteps = () => {
    return steps.length
  }

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps()
  }

  return (
    <>
      <Grow in={createAgentMutation.isSuccess} unmountOnExit>
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'center', sm: 'center' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            width: '100%',
          }}
        >
          <Grid
            container
            component="form"
            direction="row"
            rowSpacing={4}
            sx={{
              width: (theme) => theme.breakpoints.values.sm,
              border: 1,
              borderColor: grey[300],
              boxShadow: 25,
              borderRadius: 2.5,
              mt: 5,
              p: 6,
              bgcolor: 'background.paper',
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" textAlign="center">
                {t('userSignUpPage.successComponent.title')}
              </Typography>
              <Typography variant="body1" textAlign="center">
                {t('userSignUpPage.successComponent.subTitle')}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Link
                component={RouterLink}
                to={`/login${FROM ? location.search : ''}`}
                underline="none"
              >
                <Button variant="contained" sx={{ width: '100%' }}>
                  <Typography fontSize={20}>
                    {t('userSignUpPage.successComponent.goToLogin')}
                  </Typography>
                </Button>
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Grow>

      <Grow in={!createAgentMutation.isSuccess} unmountOnExit>
        <Box>
          <Grid
            container
            component={'form'}
            sx={{
              padding: (theme) => ({
                xs: theme.spacing(4, 1, 2),
                sm: theme.spacing(4, 2, 4),
                md: theme.spacing(4, 3, 6),
                lg: theme.spacing(4, 4, 8),
              }),
            }}
          >
            <Grid
              item
              // xs={12}
              container
              justifyContent="center"
            >
              <Stack
                sx={{
                  bgcolor: 'background.paper',
                  px: 2,
                  py: 4,
                  boxShadow: 25,
                  borderRadius: 2.5,
                }}
                spacing={2}
              >
                <Typography variant="h5" align="center">
                  {t('signUpPage.label')}
                </Typography>
                <Box sx={{ pt: 4 }}>
                  <Stepper
                    activeStep={activeStep}
                    sx={{
                      maxWidth: (theme) => theme.breakpoints.values.sm,
                      flexGrow: 1,
                      mx: 'auto',
                    }}
                  >
                    {steps.map((step, index) => (
                      <Step key={step.label} completed={completed[index]}>
                        <StepLabel>{t(step.label)}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
                {steps.map((step, index) => (
                  <Box
                    key={step.label}
                    sx={{
                      display: activeStep === index ? 'block' : 'none',
                    }}
                  >
                    {step.component}
                  </Box>
                ))}
                {allStepsCompleted() ? (
                  <Grid item>
                    <Grid
                      container
                      direction="column"
                      alignItems="center"
                      spacing={3}
                    >
                      <Grid item>
                        <Typography
                          align="center"
                          sx={{ mt: 2, mb: 1, whiteSpace: 'pre' }}
                        >
                          {t('signUpPage.message')}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <LinkButton
                          replace
                          LinkComponent={Link}
                          variant="contained"
                          color="primary"
                          to="/sign-in"
                        >
                          {t('signUpPage.controls.final')}
                        </LinkButton>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : null}
                <Grid item>
                  <Collapse in={!!localIsError}>
                    <Alert
                      severity="error"
                      onClose={handleCloseError}
                      sx={{ maxWidth: 600, mx: 'auto' }}
                    >
                      {t('error.unspecific')}
                    </Alert>
                  </Collapse>
                </Grid>
                <Grid item container>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      pt: 2,
                      maxWidth: (theme) => theme.breakpoints.values.sm,
                      flexGrow: 1,
                      mx: 'auto',
                    }}
                  >
                    {allStepsCompleted() ? null : (
                      <StyledButton
                        variant="outlined"
                        color="secondary"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{
                          mr: 1,
                          visibility: activeStep === 0 ? 'hidden' : '',
                        }}
                      >
                        {t('signUpPage.controls.back')}
                      </StyledButton>
                    )}
                    <Box sx={{ flex: '1 1 auto' }} />
                    <StyledButton
                      variant="contained"
                      onClick={handleNext}
                      sx={{
                        mr: 1,
                        pointerEvents: isSubmitting ? 'none' : 'auto',
                      }}
                    >
                      {isLastStep() ? (
                        createAgentMutation.isLoading ? (
                          <CircularProgress
                            sx={{
                              '& svg': {
                                color: 'background.paper',
                              },
                            }}
                            size={30}
                          />
                        ) : (
                          t('signUpPage.controls.complete')
                        )
                      ) : isSubmitting ? (
                        <CircularProgress
                          sx={{
                            '& svg': {
                              color: 'white',
                            },
                          }}
                          size={30}
                        />
                      ) : (
                        t('signUpPage.controls.next')
                      )}
                    </StyledButton>
                  </Box>
                </Grid>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Grow>
    </>
  )
}

export default SignUpPage
